import { Button, Col, Form, Input, Space, Table, TableColumnsType, Typography } from 'antd'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import React, { useEffect, useState } from 'react'
import { useWatch } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { DeleteOutlined, SwapOutlined } from '@ant-design/icons'
import { red } from '@ant-design/colors'
import { useTranslation } from 'react-i18next'
import { TransactionFormFields, VoucherFormFields } from './VoucherForm'
import { FormattedInputNumber } from '../../../../components/Misc/FormattedInputNumber'
import { calculateTotalTrasactionsValueCents } from './utils'
import { formatValueByType } from '../../../../utils/helpers'
import AccountSelect from '../../../../features/account/components/AccountSelect'
import ToolBar from '../../../../components/Misc/ToolBar'

const { Text, Title } = Typography

interface Transaction {
  id?: string
  accountCode: string
  description: string
  debit?: number
  credit?: number
}

interface TransactionTableFormProps {
  copyVoucher?: boolean
}

const TransactionTableForm: React.FC<TransactionTableFormProps> = ({ copyVoucher }) => {
  const { t } = useTranslation()
  const form = useFormInstance<VoucherFormFields>()
  const [version, setVersion] = React.useState(0)
  const watchedId = useWatch('id', form)
  const watchedTransactions = useWatch('transactions', form) || []
  const [mirrored, setMirrored] = useState<boolean>(false)

  const swapAllDebitCredit = () => {
    setMirrored(!mirrored)
    const currentTransactions = form.getFieldValue('transactions') || []
    const swappedTransactions = currentTransactions.map((transaction: Transaction) => ({
      ...transaction,
      debit: transaction.credit ?? undefined,
      credit: transaction.debit ?? undefined
    }))
    form.setFieldsValue({ transactions: swappedTransactions })
    setVersion(prev => prev + 1)
    form.validateFields()
  }

  useEffect(() => {
    setMirrored(false)
  }, [watchedId])

  const columns: TableColumnsType<TransactionFormFields> = [
    {
      title: t('global:account'),
      dataIndex: 'id',
      hidden: true,
      render: (text, record, index) => (
        <FormItem noStyle name={[index, 'id']} hidden>
          <Input />
        </FormItem>
      )
    },
    {
      title: t('global:account'),
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 300,
      render: (text, record, index) => (
        <FormItem noStyle name={[index, 'accountCode']} rules={[{ required: true }]}>
          <AccountSelect style={{ width: '100%', maxWidth: 300 }} />
        </FormItem>
      )
    },
    {
      title: t('global:description'),
      dataIndex: 'description',
      key: 'description',
      render: (text, record, index) => (
        <FormItem noStyle name={[index, 'description']} rules={[{ required: true }]}>
          <Input />
        </FormItem>
      )
    },
    {
      title: t('vouchersPage:debit'),
      dataIndex: 'debit',
      key: 'debit',
      width: 150,
      render: (text, record, index) => (
        <FormItem
          key={`debit-${index}-${version}`}
          noStyle
          name={[index, 'debit']}
          rules={[{ required: !form.getFieldValue(['transactions', index, 'credit']) }]}
        >
          <FormattedInputNumber precision={2} min={0} style={{ width: '100%' }} />
        </FormItem>
      )
    },
    {
      title: t('vouchersPage:credit'),
      dataIndex: 'credit',
      key: 'credit',
      width: 150,
      render: (text, record, index) => {
        return (
          <FormItem
            key={`credit-${index}-${version}`}
            noStyle
            name={[index, 'credit']}
            rules={[{ required: !form.getFieldValue(['transactions', index, 'debit']) }]}
          >
            <FormattedInputNumber precision={2} min={0} style={{ width: '100%' }} />
          </FormItem>
        )
      }
    },
    {
      title: t('global:actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 100,
      align: 'center'
    }
  ]

  const toolBar = () => {
    return (
      <ToolBar style={{ justifyContent: 'space-between', paddingBottom: 0 }}>
        <Col>
          <Title level={5}>{t('vouchersPage:voucherTransactions')}</Title>
        </Col>
        <Col>
          <Space.Compact block>
            {copyVoucher && (
              <Button
                type={mirrored ? 'primary' : 'default'}
                icon={<SwapOutlined />}
                onClick={swapAllDebitCredit}
                style={{ minWidth: 140 }}
              >
                {t('vouchersPage:mirrored')}
              </Button>
            )}
          </Space.Compact>
        </Col>
      </ToolBar>
    )
  }

  return (
    <Form.List
      name="transactions"
      rules={[
        {
          validator: async (__, pageData) => {
            const { difference } = calculateTotalTrasactionsValueCents(pageData)

            if (difference === 0) {
              return Promise.resolve()
            }

            return Promise.reject(new Error(t('vouchersPage:voucherError')))
          }
        }
      ]}
    >
      {(fields, { add, remove }, { errors }) => {
        const transactions = watchedTransactions
        const enhancedColumns = columns.map(col => ({
          ...col,
          render:
            col.key === 'actions'
              ? (text: any, record: any, index: number) => (
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      remove(index)
                      setVersion(prev => prev + 1)
                    }}
                  />
                )
              : col.render
        }))

        return (
          <>
            {toolBar()}

            <Table
              columns={enhancedColumns}
              dataSource={transactions.map((transaction, index) => ({ ...transaction, index }))}
              pagination={false}
              rowKey={(record: TransactionFormFields & { index?: number }) => `transaction-${record.index}-${version}`}
              locale={{ emptyText: t('vouchersPage:noTransactions') }}
              summary={pageData => {
                const { totalCredit, totalDebit, difference } = calculateTotalTrasactionsValueCents(pageData)
                const format = (val: number) =>
                  formatValueByType(val / 100, undefined, {
                    maximumFractionDigits: 2
                  })
                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={1} colSpan={6}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button type="dashed" block onClick={() => add()}>
                            {t('global:add')}
                          </Button>
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={2} index={0}>
                        {t('customReportPage:sum')}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right" index={1}>
                        <Text>{format(totalDebit)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right" colSpan={1} index={2}>
                        <Text>{format(totalCredit)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3} index={0}>
                        {t('global:difference')}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right" colSpan={1} index={1}>
                        <Text>{format(difference) || 0}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    {(errors?.length || 0) > 0 ? (
                      <Table.Summary.Row style={{ backgroundColor: red[1] }}>
                        <Table.Summary.Cell colSpan={5} index={0}>
                          <Form.ErrorList errors={errors} />
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    ) : null}
                  </>
                )
              }}
            />
          </>
        )
      }}
    </Form.List>
  )
}

export default TransactionTableForm
